import useIsMobile from 'hooks/utils/useIsMobile';
import { useEffect, useContext, useState, Fragment } from 'react';
import CardItem from 'components/sections/highlights/cardItem';
import Cta from 'components/sections/cta';
import { FilterContext } from 'hooks/filterGlobalState/useFilterGlobalState';
import useCollectionSelectItemTracking from 'hooks/tracking/useCollectionSelectItemTracking';
import getShopifyData from 'lib/shopify/getShopifyData';
import useCollectionViewItemListTracking from 'hooks/tracking/useCollectionViewItemListTracking';
import { useSettings } from 'hooks';
import cn from 'classnames';
import styles from './collectionProducts.module.scss';
import InView from '../../../shared/inView';

const CollectionProducts = ({
  ctasToAppear,
  locale,
  globalModules,
  pageTitle,
  showAttributeLabel,
  attributeLabelBackgroundColor,
  attributeLabelTextColor,
  products,
}) => {
  const isMobile = useIsMobile();
  const [filterState] = useContext(FilterContext);
  const { setSaveItemSelectionList } = useSettings();
  const [pageProducts, setPageProducts] = useState(products);
  const [shopifyData, setShopifyData] = useState([]);
  const localesWithWebshop = globalModules?.settings?.localesWithWebshop;

  useEffect(() => {
    setShopifyData([]);
    const regroupShopifyProducts = currentData => {
      const result = [];
      if (currentData?.length > 0) {
        // updating prices directly in filtered products which are stored in context
        products.forEach(prod => {
          let cardData;
          if (prod.products) {
            const bundle = [];
            prod.products.forEach(item => {
              const bundleProd = currentData.find(
                ({ productSKU }) => item.shopifyData?.variants?.[0]?.sku === productSKU
              );
              if (bundleProd) {
                bundle.push(bundleProd);
              }
            });
            // eslint-disable-next-line no-param-reassign
            cardData = bundle;
          } else {
            const data = currentData.find(
              ({ productSKU }) => prod?.shopifyData?.variants?.[0]?.sku === productSKU
            );
            // eslint-disable-next-line no-param-reassign
            cardData = data;
          }

          result.push({ id: prod.id, cardData });
        });
        setShopifyData(result);
      }
    };

    const shopifyDataRetrieval = async () => {
      if (products.length > 0) {
        const productIds = products.reduce((rez, highlight) => {
          if (highlight.products) {
            return [
              ...rez,
              ...highlight.products.map(
                bundleProd =>
                  `gid://shopify/Product/${bundleProd.shopifyData?.variants[0].product_id}`
              ),
            ];
          }

          return [...rez, `gid://shopify/Product/${highlight.shopifyData?.variants[0].product_id}`];
        }, []);

        const currentData = await getShopifyData({ productIds, locale });

        regroupShopifyProducts(currentData);
      }
    };
    if (localesWithWebshop?.onOrOff) {
      shopifyDataRetrieval();
    }
  }, [products, localesWithWebshop?.onOrOff, locale]);

  useEffect(() => {
    const updateProductData = (product, updatedShopifyData) => {
      if (product.shopifyData) {
        // eslint-disable-next-line no-param-reassign
        product.shopifyData.variants[0] = {
          ...product?.shopifyData?.variants?.[0],
          ...(updatedShopifyData?.productCompareAtPrice && {
            compareAtPrice: updatedShopifyData?.productCompareAtPrice,
          }),
          outOfStock: updatedShopifyData?.outOfStock,
          ...(updatedShopifyData?.productPrice && { price: updatedShopifyData?.productPrice }),
          ...(updatedShopifyData?.productTitle && { title: updatedShopifyData?.productTitle }),
        };
      }
    };

    if (shopifyData.length) {
      filterState.filteredProducts?.forEach(prod => {
        const shopifyProduct = shopifyData.find(({ id }) => id === prod.id);
        if (shopifyProduct) {
          if (prod.products) {
            prod.products.forEach(bundleProd => {
              const bundleProductData = shopifyProduct.cardData.find(
                ({ productSKU }) => productSKU === bundleProd?.shopifyData?.variants?.[0]?.sku
              );
              updateProductData(bundleProd, bundleProductData);
            });
          } else {
            updateProductData(prod, shopifyProduct.cardData);
          }
          // eslint-disable-next-line no-param-reassign
          prod.cardShopifyData = shopifyProduct.cardData;
        }
      });

      setPageProducts(filterState.filteredProducts);
    } else {
      setPageProducts(filterState.filteredProducts);
    }
  }, [filterState.filteredProducts, shopifyData]);

  let ctaCount = 0;

  const ctaCanAppear = index => {
    // On desktop, if the 12th product is being mapped, return true
    if (!isMobile) {
      if (index > 0 && index % 10 === 0) {
        ctaCount += 1;
        return true;
      }
    }
    // On mobile, if the 8th product is being mapped, return true
    else if (isMobile) {
      if (index > 0 && index % 8 === 0) {
        ctaCount += 1;
        return true;
      }
    }

    return false;
  };

  // If device isn't mobile, decide CSS class based on index of product. Otherwise default to class #1
  const decideCtaClass = index => {
    if (!isMobile) {
      if (index % 10 === 0 && index % 20 !== 0) return styles.inBetweenCtaOne;
      if (index % 20 === 0 && index % 10 === 0) return styles.inBetweenCtaTwo;
    } else if (isMobile) return styles.inBetweenCtaOne;
    return null;
  };

  const { pushToDataLayerCb } = useCollectionViewItemListTracking({
    products: filterState.filteredProducts,
    itemListName: `Category: ${pageTitle}`,
  });

  const trackSelectItem = (product, index) => {
    if (localesWithWebshop?.onOrOff) {
      if (product.type === 'PageProduct') {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useCollectionSelectItemTracking({
          product: {
            name: product?.cardShopifyData?.productTitle,
            id: product.shopifyData?.variants?.[0]?.product_id,
            category: product?.cardShopifyData?.productVendor,
            price: product?.cardShopifyData?.productPrice,
            position: index,
          },
          list: `Category: ${pageTitle}`,
          setSaveItemSelectionList,
          locale,
        });
      }

      if (product.type === 'PageProductBundle') {
        product?.cardShopifyData?.forEach((bundleProduct, i) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useCollectionSelectItemTracking({
            product: {
              name: bundleProduct?.productTitle,
              id: product.products[i].shopifyData?.variants?.[0]?.product_id,
              category: bundleProduct?.productVendor,
              price: bundleProduct?.productPrice,
              position: index,
            },
            list: `Category: ${pageTitle}`,
            setSaveItemSelectionList,
            locale,
          });
        });
      }
    }
  };

  return (
    <div className={styles.products}>
      {pageProducts?.map((product, index) => (
        <Fragment key={product.shopifyData?.variants?.[0]?.product_id}>
          <InView
            className={styles.individualProduct}
            index={index}
            pushToDataLayerCb={pushToDataLayerCb}
            product={product}
            localesWithWebshop={localesWithWebshop}
          >
            <CardItem
              onClick={() => trackSelectItem(product, index)}
              locale={locale}
              key={product.id}
              itemData={product}
              shopifyProduct={product?.cardShopifyData}
              globalModules={globalModules}
              itemIndex={index}
              pageTitle={pageTitle}
              showAttributeLabel={showAttributeLabel}
              attributeLabelBackgroundColor={attributeLabelBackgroundColor}
              attributeLabelTextColor={attributeLabelTextColor}
            />
          </InView>
          {/** The -1 in the data property is to make ctaCount zero-based again in comparison to the ctasToAppear array */}
          {ctaCanAppear(index) && ctasToAppear.length > 0 && (
            <Cta
              style={{
                '--roundedIndex': isMobile ? 5 + (ctaCount - 1) * 5 : 3 + (ctaCount - 1) * 3,
              }}
              locale={locale}
              className={cn(decideCtaClass(index), {
                [styles.webshopCta]: localesWithWebshop?.onOrOff,
              })}
              data={ctasToAppear[(ctaCount - 1) % ctasToAppear.length]}
              isCollection
            />
          )}
        </Fragment>
      ))}
    </div>
  );
};

export default CollectionProducts;
